import autobind from 'class-autobind';
import isEqual from 'lodash/isEqual';

import {
  DIVIDEND_RECALC,
  DIVIDENDS,
  MANUAL_RECALC,
  OWNWINPERCENTAGE_RECALC,
  PRESET_REDUCTION_RECALC,
  TIPPED_WINNERS,
} from 'atg/constants/BetUpdateType';
import * as Events from 'atg/constants/Events';
import {
  changeCurrentBet,
  changeCurrentBetPresetBasedAt,
} from 'atg/redux/actions/betActions';
import observeStore from 'atg/redux/observeStore';
import { stomp as stompSelectors } from 'atg/redux/selectors';
import EventDispatcher from 'atg/utils/EventDispatcher';
import MomentDate from 'atg/utils/MomentDate';
import Request from 'atg/utils/Request';
import * as Utils from 'atg/utils/Utils';
import * as validators from 'atg/utils/validators';
import { ATG_UPDATE } from 'shared/constants/StompKind';
import {
  addSubscription,
  removeSubscription,
  toggleManualUpdateButton,
} from 'shared/redux/actions';
import {
  menu as menuSelectors,
  user as userSelectors,
} from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';

import BetInformation from './BetInformation';
import BetKeyInfo from './BetKeyInfo';
import CreateBetParameters from './CreateBetParameters';
import GameInformation from './GameInformation';
import GameUpdate from './GameUpdate';
import HorseInfoSet from './HorseInfoSet';
import JockeyInformation from './JockeyInformation';
import MyDividendInformation from './MyDividendInformation';
import RaceAnalysis from './RaceAnalysis';
import RaceInformation from './RaceInformation';
import ResultInformation from './ResultInformation';
import ResultUpdate from './ResultUpdate';
import RowsLeftInformation from './RowsLeftInformation';
import SavedBetSet from './SavedBetSet';
import SavedTemplateSet from './SavedTemplateSet';
import Stake from './Stake';

// ---------------------------------------- API CLASS ----------------------------------------

let _cache = {};
const CACHE_MEDIUM = 60 * 5;

function setCache(key, response, cacheTime) {
  const cacheValue = cacheTime || CACHE_MEDIUM;
  const momentDate = new MomentDate();

  _cache[key] = {
    response: response,
    cacheInvalidate: momentDate.moment.add(cacheValue, 'seconds'),
  };
}

function getCache(key) {
  const cache = _cache[key];
  if (!cache) {
    return false;
  }

  const momentDate = new MomentDate();
  if (momentDate.moment.diff(cache.cacheInvalidate, 'seconds') < 0) {
    return cache.response;
  }
  return false;
}

function clearCacheByKey(key) {
  delete _cache[key];
}

class API {
  constructor() {
    autobind(this);
    API.instance = this;
    this.stompTopics = [];
    this.reset();

    observeStore(getStore(), this.onLoggedInChanged, userSelectors.isLoggedIn);
  }

  setup(delegate) {
    this.delegate = delegate;
    this.destroyStompObserver = observeStore(
      getStore(),
      this.onStompStateChanged,
      stompSelectors.getState,
    );
  }

  teardown() {
    this.delegate = null;
    this.destroyStompObserver();
    this.stompTopics.forEach(topic =>
      getStore().dispatch(removeSubscription(topic)),
    );
    this.stompTopics = [];

    if (menuSelectors.manualUpdateButtonActive(getStore().getState())) {
      getStore().dispatch(toggleManualUpdateButton());
    }
  }

  reset() {
    this.resetStake();
    this.resetRaceInformation();
    this.resetResultInformation();
    this.resetBetInformation();
    this.resetGameInformation();
    this.resetMyDividendInformation();
    this.resetRowsLeftInformation();
  }

  clearCache() {
    _cache = {};
  }

  onLoggedInChanged(isLoggedIn, prevIsLoggedIn) {
    if (prevIsLoggedIn && !isLoggedIn) {
      this.reset();
      this.clearCache();
    }
  }

  // -------------------------- PSF ---------------------------

  setPointsFromSource(sourceName, pointIndex, successCallback) {
    const pathMap = {
      pointAsTrioIndex: 'game/setTrioIndexPoints',
      pointAsOdds: 'game/setOddsPoints',
      pointAsRank: 'game/setRankingPoints',
      pointAsPercent: 'game/setBetPercentPoints',
      pointAsInsats30m: 'game/setTrendPoints/30',
      pointAsInsats1t: 'game/setTrendPoints/60',
      pointAsInsats2t: 'game/setTrendPoints/120',
      pointAsOwnWin: 'game/setOwnRankingPoints',
      pointAsWinnerPercentRanking: 'game/setWinnerPercentRankingPoints',
      pointAsEarningsRanking: 'game/setPriceMoneyRankingPoints',
      pointAsStartPointRanking: 'game/setStartPointRankingPoints',
      pointAsEarningsPerStartRanking: 'game/setEarningsPerStartRankingPoints',
      pointAsRecordTimeRanking: 'game/setRecordTimeRankingPoints',
    };

    const path = pathMap[sourceName];

    if (path == null) {
      console.warn(`Unexpected sourceName argument '${sourceName}'`);
      return;
    }

    const request = new Request({
      path: `${path}/${pointIndex}/`,
      method: 'GET',
      onSuccess: request => {
        successCallback(request.responseData);
      },
    });
    request.start();
    return request;
  }

  // ---------------------------------------- EXPORT ----------------------------------------

  uploadToRikstoto(callback) {
    new Request({
      path: 'game/uploadRikstoto',
      onSuccess: function (request) {
        callback(request.responseData);
      },
    }).start();
  }

  exportXML(callback) {
    new Request({
      path: 'game/generateXML',
      onSuccess: function (request) {
        callback(request.responseData);
      },
    }).start();
  }

  fetchXML(fetchURL, callback) {
    new Request({
      path: fetchURL,
      onSuccess: function (request) {
        callback(request.responseData);
      },
    }).start();
  }

  createSystemReportPDF(callback) {
    const numResultRowsShown = this.betInformation
      .getBetConfig()
      .getShowResultRows().length;
    const numTotalRows = this.betInformation.getNumberOfRows();
    // Include results if the user has selected to show results for all rows
    const includeResults = numResultRowsShown === numTotalRows;

    new Request({
      path: `report/betSystemReport/${includeResults}`,
      method: 'GET',
      onSuccess: function (request) {
        if (request.responseData) {
          callback(request.responseData);
        } else {
          callback(false);
        }
      },
      onError: function () {
        callback(false);
      },
    }).start();
  }

  // ---------------------------------------- SHARED BETS ----------------------------------------

  fetchSharedBet(betCode, callback) {
    new Request({
      path: `game/fetchBetToWatch/${betCode}`,
      method: 'GET',
      onSuccess: function (request) {
        if (request.responseData) {
          this.onLoadedBetInformation(request);
          callback(true);
        } else {
          callback(false);
        }
      }.bind(this),
      onError: function () {
        callback(false);
      },
    }).start();
  }

  // ---------------------------------------- SAVED BETS ----------------------------------------

  loadSavedBets(callback) {
    new Request({
      path: 'archive/fetchSavedBets',
      onSuccess: function (request) {
        if (request.responseData) {
          const betSets = {
            currentBets: new SavedBetSet(request.responseData.currentBets),
            earlierBets: new SavedBetSet(request.responseData.earlierBets),
            uploadedBets: new SavedBetSet(request.responseData.uploadedBets),
          };
          callback(true, betSets);
        } else {
          callback(false);
        }
      },
      onError: function () {
        callback(false);
      },
    }).start();
  }

  // TODO - new resource needed server side to delete multiple in one request
  deleteSavedBets(betIds) {
    let n = betIds.length;

    function done() {
      n -= 1;
      if (n === 0) {
        EventDispatcher.emit(Events.ARCHIVE_DELETE_COMPLETED);
      }
    }

    betIds.forEach(function (id) {
      new Request({
        path: 'archive/deleteBetById',
        method: 'POST',
        data: {
          betId: id,
        },
        onSuccess: done,
        onError: done,
      }).start();
    });
  }

  duplicateSavedBet(savedBet, callback) {
    new Request({
      path: 'archive/duplicateBet',
      method: 'POST',
      data: {
        betId: savedBet.getID(),
      },
      onSuccess: function (request) {
        if (request.responseData) {
          this.onLoadedBetInformation(request);
          callback(request.responseData.id);
        } else {
          callback(null);
        }
      }.bind(this),
      onError: function () {
        callback(null);
      },
    }).start();
  }

  // ---------------------------------------- SAVED TEMPLATES ----------------------------------------

  loadSavedTemplates(callback) {
    const path = 'template/fetchSavedTemplates';
    const cached = getCache(path);
    if (cached) {
      callback(true, cached);
    } else {
      new Request({
        path: path,
        onSuccess: function (request) {
          if (request.responseData) {
            const set = new SavedTemplateSet(request.responseData);
            setCache(path, set, CACHE_MEDIUM);
            callback(true, set);
          } else {
            callback(false);
          }
        },
        onError: function () {
          return callback(false);
        },
      }).start();
    }
  }

  saveTemplate(id, name, reductions, marks, successCallback) {
    clearCacheByKey('template/fetchSavedTemplates');
    new Request({
      path: '/template/createTemplate',
      method: 'POST',
      data: {
        id: id,
        name: name,
        reductions: reductions,
        marks: marks,
      },
      onSuccess: function () {
        if (successCallback) {
          successCallback();
        }
      },
    }).start();
  }

  // TODO - new resource needed server side to delete multiple in one request
  deleteTemplates(templateIds) {
    clearCacheByKey('template/fetchSavedTemplates');

    let n = templateIds.length;

    function done() {
      n -= 1;
      if (n === 0) {
        EventDispatcher.emit(Events.ARCHIVE_DELETE_COMPLETED);
      }
    }

    templateIds.forEach(function (id) {
      new Request({
        path: 'template/deleteTemplateById',
        method: 'POST',
        data: {
          id: id,
        },
        onSuccess: done,
        onError: done,
      }).start();
    });
  }

  // ---------------------------------------- GAME SUGGESTIONS ----------------------------------------

  loadBetTip(id, callback) {
    new Request({
      path: `game/loadRecommendedBet/${id}`,
      method: 'GET',
      onSuccess: function (request) {
        if (request.responseData) {
          this.onLoadedBetInformation(request);
          callback(request.responseData.id);
        } else {
          callback(null);
        }
      }.bind(this),
      onError: function () {
        callback(null);
      },
    }).start();
  }

  // ---------------------------------------- GAME INFORMATION ----------------------------------------

  resetGameInformation() {
    this.gameInformation = new GameInformation();
  }

  getUpdateGameInformationRequest() {
    return new Request({
      path: 'game/fetchGame',
      onSuccess: this.onGameInformationUpdatesReceived,
    });
  }

  onGameInformationUpdatesReceived(requestOrData) {
    let data =
      requestOrData instanceof Request
        ? requestOrData.responseData
        : requestOrData;

    if (!data) {
      data = {};
    }

    const gameUpdate = new GameUpdate(data);
    this.gameInformation.setTimestamp(gameUpdate.getTimestamp());

    const numberOfLegUpdates = gameUpdate.getNumberOfLegUpdates();

    const newlyScratchedHorseLegs = new Set();
    const hadExistingGameInfo = this.gameInformation.getNumberOfRows() > 0;

    for (
      let legUpdateIndex = 0;
      legUpdateIndex < numberOfLegUpdates;
      legUpdateIndex++
    ) {
      const legUpdate = gameUpdate.getLegUpdate(legUpdateIndex);
      const legIndex = legUpdate.getLegIndex();

      while (this.gameInformation.getNumberOfRows() < legIndex + 1) {
        this.gameInformation.addRow();
      }

      const gameRow = this.gameInformation.getRow(legIndex);

      if (gameRow) {
        if (legUpdate.hasWinnerTurnover()) {
          gameRow.setWinnerTurnover(legUpdate.getWinnerTurnover());
        }

        const numberOfStartUpdates = legUpdate.getNumberOfStartUpdates();

        for (
          let startUpdateIndex = 0;
          startUpdateIndex < numberOfStartUpdates;
          startUpdateIndex++
        ) {
          const startUpdate = legUpdate.getStartUpdate(startUpdateIndex);
          const startIndex = startUpdate.getStartIndex();

          if (startUpdate.hasMarkPercent()) {
            gameRow.setMarkPercent(startIndex, startUpdate.getMarkPercent());
          }

          if (startUpdate.hasOdds()) {
            gameRow.setOdds(startIndex, startUpdate.getOdds());
          }

          if (startUpdate.hasTrioIndex()) {
            gameRow.setTrioIndex(startIndex, startUpdate.getTrioIndex());
          }

          if (startUpdate.hasScratched()) {
            const wasScratched = gameRow.isScratched(startIndex);
            const isScratched = startUpdate.getScratched();
            gameRow.setScratched(startIndex, isScratched);
            if (isScratched && !wasScratched && hadExistingGameInfo) {
              newlyScratchedHorseLegs.add(legIndex);
            }
          }

          if (startUpdate.hasShoeInfo()) {
            gameRow.setShoeInfo(startIndex, startUpdate.getShoeInfo());
          }

          if (startUpdate.hasSulkyInfo()) {
            gameRow.setSulkyInfo(startIndex, startUpdate.getSulkyInfo());
          }

          if (startUpdate.isFirstStart()) {
            gameRow.setIsFirstStart(startIndex, true);
          }
        }

        Utils.callDelegate(this.delegate, 'onAPIUpdatedGameRow', legIndex);
      }
    }

    if (gameUpdate.hasTurnover()) {
      this.gameInformation.setTurnover(gameUpdate.getTurnover());
    }

    this.gameInformation.setHasOdds(gameUpdate.hasOdds());

    if (gameUpdate.hasDividendPerHorse()) {
      this.gameInformation.setDividendPerHorse(
        gameUpdate.getDividendPerHorse(),
      );
    }

    if (gameUpdate.hasDividendPerHorsePerLeg()) {
      this.gameInformation.setDividendPerHorsePerLeg(
        gameUpdate.getDividendPerHorsePerLeg(),
      );
    }

    Utils.callDelegate(this.delegate, 'onAPIUpdatedGameInformation');

    if (newlyScratchedHorseLegs.size > 0) {
      this.getLoadStakeRequest(success => {
        if (success) {
          newlyScratchedHorseLegs.forEach(legIndex =>
            Utils.callDelegate(this.delegate, 'onAPIUpdatedGameRow', legIndex),
          );
        }
      }).start();
    }
  }

  // ---------------------------------------- RESULT INFORMATION ----------------------------------------

  resetResultInformation() {
    this.resultInformation = new ResultInformation();
  }

  updateResultInformation() {
    this.getUpdateResultInformationRequest().start();
  }

  getUpdateResultInformationRequest() {
    return new Request({
      path: 'game/fetchResult',
      onSuccess: this.onResultInformationUpdatesReceived,
    });
  }

  onResultInformationUpdatesReceived(requestOrData) {
    let data =
      requestOrData instanceof Request
        ? requestOrData.responseData
        : requestOrData;

    if (!data) {
      data = {
        isResultComplete: false,
      };
    }

    const resultUpdate = new ResultUpdate(data);
    this.resultInformation.setTimestamp(resultUpdate.getTimestamp());

    if (resultUpdate.hasResultType()) {
      this.resultInformation.setResultType(resultUpdate.getResultType());
    }

    if (resultUpdate.hasIsResultComplete()) {
      this.resultInformation.setResultComplete(
        resultUpdate.getIsResultComplete(),
      );
    }

    if (resultUpdate.hasNumberOfSystems()) {
      this.resultInformation.setNumberOfSystems(
        resultUpdate.getNumberOfSystems(),
      );
    }

    if (resultUpdate.hasTurnover()) {
      this.resultInformation.setTurnover(resultUpdate.getTurnover());
    }

    if (resultUpdate.hasOdds()) {
      this.resultInformation.setOdds(resultUpdate.getOdds());
    }

    const numberOfLegUpdates = resultUpdate.getNumberOfLegUpdates();

    for (
      let legUpdateIndex = 0;
      legUpdateIndex < numberOfLegUpdates;
      legUpdateIndex++
    ) {
      const legUpdate = resultUpdate.getLegUpdate(legUpdateIndex);
      const legIndex = legUpdate.getLegIndex();

      while (this.resultInformation.getNumberOfRows() < legIndex + 1) {
        this.resultInformation.addRow();
      }

      const resultRow = this.resultInformation.getRow(legIndex);
      if (resultRow) {
        if (legUpdate.hasSystemsLeft()) {
          resultRow.setSystemsLeft(legUpdate.getSystemsLeft());
        }

        if (legUpdate.hasValue()) {
          resultRow.setValue(legUpdate.getValue());
        }

        if (legUpdate.hasWinners()) {
          resultRow.setWinners(legUpdate.getWinners());
        }

        Utils.callDelegate(this.delegate, 'onAPIUpdatedResultRow', legIndex);
      }
    }

    const numberOfPayoutUpdates = resultUpdate.getNumberOfPayoutUpdates();

    for (
      let payoutUpdateIndex = 0;
      payoutUpdateIndex < numberOfPayoutUpdates;
      payoutUpdateIndex++
    ) {
      const payoutUpdate = resultUpdate.getPayoutUpdate(payoutUpdateIndex);
      const numberOfPayouts = this.resultInformation.getNumberOfPayouts();
      let payoutToUpdate = null;

      for (let payoutIndex = 0; payoutIndex < numberOfPayouts; payoutIndex++) {
        const payout = this.resultInformation.getPayout(payoutIndex);
        if (
          payout.getNumberOfCorrects() === payoutUpdate.getNumberOfCorrects()
        ) {
          payoutToUpdate = payout;
          break;
        }
      }

      if (!payoutToUpdate) {
        payoutToUpdate = this.resultInformation.addPayout();
        payoutToUpdate.setNumberOfCorrects(payoutUpdate.getNumberOfCorrects());
      }

      if (payoutUpdate.hasPrizeGroup()) {
        payoutToUpdate.setPrizeGroup(payoutUpdate.getPrizeGroup());
      }

      if (payoutUpdate.hasNumberOfSystems()) {
        payoutToUpdate.setNumberOfSystems(payoutUpdate.getNumberOfSystems());
      }

      if (payoutUpdate.hasPayoutAmount()) {
        payoutToUpdate.setPayoutAmount(payoutUpdate.getPayoutAmount());
      }

      if (payoutUpdate.hasTotalAmount()) {
        payoutToUpdate.setTotalAmount(payoutUpdate.getTotalAmount());
      }
    }

    Utils.callDelegate(this.delegate, 'onAPIUpdatedResultInformation');
    this.stompCheck();
  }

  // ---------------------------- MY DIVIDEND / ROWS LEFT INFORMATION ---------------------------------

  resetMyDividendInformation() {
    this.myDividendInformation = new MyDividendInformation();
  }

  resetRowsLeftInformation() {
    this.rowsLeftInformation = new RowsLeftInformation();
  }

  parseRowsLeftData(data) {
    const { myDividend, ...rowsLeftData } = data || {};

    if (myDividend != null) {
      this.myDividendInformation = new MyDividendInformation(data.myDividend);
      Utils.callDelegate(this.delegate, 'onAPIUpdatedMyDividends');
    }

    this.rowsLeftInformation = new RowsLeftInformation(rowsLeftData);
    Utils.callDelegate(this.delegate, 'onAPIUpdatedRowsLeftInformation');
  }

  // ---------------------------------------- RACE INFORMATION ----------------------------------------

  resetRaceInformation() {
    this.raceInformation = new RaceInformation();
  }

  getUpdateRaceInformationRequest() {
    return new Request({
      path: 'race/fetchRaces',
      onSuccess: this.onRaceInformationUpdatesReceived,
    });
  }

  onRaceInformationUpdatesReceived(request) {
    if (request.responseHeaders.headerName && request.responseHeaders.token) {
      this.CSRFHeaderName = request.responseHeaders.headerName;
      this.CSRFToken = request.responseHeaders.token;

      Utils.debug(`CSRF: ${this.CSRFToken}`);
    }

    if (request.responseData) {
      this.raceInformation = new RaceInformation(request.responseData);
      Utils.callDelegate(this.delegate, 'onAPIUpdatedRaceInformation');
    }
  }

  // ---------------------------------------- JOCKEY INFORMATION ----------------------------------------

  getUpdateJockeyInformationRequest() {
    return new Request({
      path: 'race/fetchDrivers',
      onSuccess: this.onJockeyInformationUpdated,
    });
  }

  onJockeyInformationUpdated(request) {
    if (request.responseData) {
      this.jockeyInformation = new JockeyInformation(request.responseData);
      Utils.callDelegate(this.delegate, 'onAPIUpdatedJockeyInformation');
    }
  }

  checkJockeyReductions(callback) {
    new Request({
      path: 'game/checkJockeyReductions',
      method: 'GET',
      onSuccess: function (request) {
        if (request.responseData) {
          callback(request.responseData);
        } else {
          callback(false);
        }
      },
      onError: function () {
        callback(false);
      },
    }).start();
  }

  // ---------------------------------------- SOCKETS ----------------------------------------

  stompCheck() {
    const topics = [];
    let topic;

    if (
      this.resultInformation &&
      !this.resultInformation.isEmpty() &&
      !this.resultInformation.isResultComplete()
    ) {
      const betKey = this.betInformation.getBetKey();
      const nowTime = new MomentDate().getTime();
      const oneHourTime = 1000 * 60 * 60;
      const minTime = nowTime - oneHourTime * 24 * 3;
      const raceTime = betKey.getDate().getTime();

      if (raceTime >= minTime) {
        topic = this.getStompTopic('gameUpdates');
        if (topic) {
          topics.push(topic);
        }

        topic = this.getStompTopic('resultUpdates');
        if (topic) {
          topics.push(topic);
        }
      }
    }

    topic = this.getStompTopic('rowsLeft');
    if (topic) {
      topics.push(topic);
    }

    topics.forEach(topic => {
      if (!this.stompTopics.includes(topic)) {
        this.stompTopics.push(topic);
        getStore().dispatch(
          addSubscription(topic, ATG_UPDATE, { atgTopic: topic }),
        );
      }
    });

    this.stompTopics
      .filter(topic => !topics.includes(topic))
      .forEach(removeTopic => {
        getStore().dispatch(removeSubscription(removeTopic));
        this.stompTopics = this.stompTopics.filter(
          topic => topic !== removeTopic,
        );
      });
  }

  getStompTopic(type) {
    const parts = [];

    if (type === 'rowsLeft') {
      const betId = this.betInformation.getID();
      if (betId) {
        parts.push(betId);
      }
    } else {
      const betKey = this.betInformation.getBetKey();
      if (betKey) {
        const date = betKey.getDate();
        if (date instanceof MomentDate) {
          parts.push(date.toISOString(true).slice(0, 10));
        }

        const trackKey = betKey.getTrackKey();
        if (trackKey) {
          parts.push(trackKey);
        }

        const betType = betKey.getBetType();
        if (betType) {
          parts.push(betType);
        }
      }

      const betConfig = this.betInformation.getBetConfig();
      if (betConfig) {
        const raceNumber = betConfig.getRaceNumber();
        if (raceNumber) {
          parts.push(raceNumber);
        }
      }
    }

    return parts.length > 0 ? `/topic/${type}-${parts.join('-')}` : null;
  }

  onStompStateChanged(state) {
    if (state == null) {
      return;
    }

    const { topic, data } = state;
    switch (topic) {
      case this.getStompTopic('gameUpdates'):
        this.onGameInformationUpdatesReceived(data);

        break;

      case this.getStompTopic('resultUpdates'):
        this.onResultInformationUpdatesReceived(data);

        break;

      case this.getStompTopic('rowsLeft'):
        this.parseRowsLeftData(data);

        break;

      default:
      // Do nothing
    }
  }

  // ---------------------------------------- CSRF ----------------------------------------

  getCSRFHeaders() {
    const headers = {};
    if (this.CSRFHeaderName && this.CSRFToken) {
      headers[this.CSRFHeaderName] = this.CSRFToken;
    }
    return headers;
  }

  // ---------------------------------------- BET INFORMATION ----------------------------------------

  resetBetInformation() {
    this.betInformation = new BetInformation();
    this.stompCheck();
  }

  setBetInformation(data) {
    const value = data instanceof BetInformation ? data.data : data;
    this.betInformation.data = value;
    this.stompCheck();
  }

  fetchBetInformation(betId, callback) {
    new Request({
      path: `archive/fetchBetById/${betId}`,
      onSuccess: request => {
        this.onLoadedBetInformation(request);
        if (callback) {
          callback(true);
        }
      },
      onError: request => {
        if (callback) {
          callback(false);
        }
        this.onFailedToLoadBetInformation(request);
      },
    }).start();
  }

  onBetAndStakeUpdate(betAndStake) {
    this.setBetInformation(betAndStake.bet);
    if (betAndStake.stake) {
      this.setStake(betAndStake.stake);
    }
    Utils.callDelegate(this.delegate, 'onAPILoadedBetInformation', true);
  }

  onLoadedBetInformation(request) {
    if (request.responseData) {
      this.resetStake();
      this.resetGameInformation();
      this.resetRaceInformation();
      this.resetResultInformation();
      this.resetMyDividendInformation();
      this.resetRowsLeftInformation();

      getStore().dispatch(changeCurrentBet(request.responseData.id));
      this.setBetInformation(request.responseData || {});
      Utils.callDelegate(this.delegate, 'onAPILoadedBetInformation', true);
    }
  }

  onFailedToLoadBetInformation() {
    this.setBetInformation({});
    Utils.callDelegate(this.delegate, 'onAPILoadedBetInformation', false);
  }

  scheduleBetUpdate(betUpdateType) {
    Utils.callDelegate(this.delegate, 'onAPIUpdateScheduled');

    this.cancelBetUpdate();
    this.betUpdateTimeoutID = window.setTimeout(
      () => this.startBetUpdate(null, betUpdateType),
      1000 * 2.5,
    );
  }

  cancelBetUpdate() {
    if (this.betUpdateTimeoutID) {
      window.clearTimeout(this.betUpdateTimeoutID);
      this.betUpdateTimeoutID = null;
    }
  }

  startBetUpdate(callback, betUpdateType) {
    this.cancelBetUpdate();

    const request = this.getUpdateBetInformationRequest(
      callback,
      betUpdateType,
    );

    if (request) {
      Utils.callDelegate(this.delegate, 'onAPIUpdateStarted');
      // Save request/callback in case this is a heavy request that
      // will be resolved later in a socket push
      this.pendingBetUpdate = {
        request,
        callback,
      };
      request.start();
    } else {
      Utils.callDelegate(this.delegate, 'onAPIUpdateIgnored');
    }
  }

  fetchDividends(callback) {
    const request = new Request({
      path: 'game/fetchDividend',
      method: 'POST',
      data: {},
      onSuccess: function (request) {
        if (request.responseData) {
          if (this.stake) {
            this.stake.data.dividend = request.responseData;
          } else {
            this.setStake({ stake: { dividend: request.responseData } });
          }
          Utils.callDelegate(this.delegate, 'onAPIUpdateComplete');
        }
        if (callback) {
          callback(true);
        }
      }.bind(this),
    });
    if (request) {
      request.start();
    }
  }

  getUpdateBetInformationRequest(callback, betUpdateType) {
    const betInformation = this.betInformation;
    const numberOfRows = betInformation.getNumberOfRows();
    const rowsData = [];
    const reductionRequirementsData = {};
    const isLocked = betInformation.getBetConfig().isLocked();
    const { pointSource = {} } = betInformation.data;

    const reductionSet = betInformation.getReductionSet();
    const presetReduction = reductionSet && reductionSet.data.presetReduction;

    let request;
    let any = false;
    let path = 'game/';

    switch (betUpdateType) {
      case DIVIDENDS:
        if (!callback) {
          path += isLocked
            ? 'updateTippedWinners'
            : 'updateBetupdateBetAndCalculateDividends';
        } else {
          path += 'updateBetupdateBetAndCalculateDividends';
        }

        break;

      case true:
        path += 'updateBetupdateBetAndCalculateDividends';

        break;

      case TIPPED_WINNERS:
        path += 'updateTippedWinners';

        break;

      case DIVIDEND_RECALC:
        path += 'updateBetStakeAmounts';

        break;

      case MANUAL_RECALC:
        path += 'recalcBet';

        break;

      case OWNWINPERCENTAGE_RECALC:
        path += 'updateOwnWinPercentage';

        break;

      default:
        path += 'updateBet';
    }

    const data = {
      pointSource,
      rows: rowsData,
      reductionRequirements: reductionRequirementsData,
    };

    if (betUpdateType === PRESET_REDUCTION_RECALC) {
      data.reductionRequirements.presetReduction = presetReduction;
    }

    request = new Request({
      path,
      method: betUpdateType === MANUAL_RECALC ? 'GET' : 'POST',
      data,
      onSuccess: request =>
        this.onUpdateBetInformationResponse(request, callback),
      onError: request =>
        this.onFailedToUpdateBetInformation(request.error, callback),
    });

    for (let rowIndex = 0; rowIndex < numberOfRows; rowIndex++) {
      const betRow = this.betInformation.getRow(rowIndex);

      if (betRow.isInvalid()) {
        any = true;
        const rowData = Utils.clone(betRow.data);
        validators.validateData(rowData);
        rowData.legNr = rowIndex + 1;
        rowsData.push(rowData);
      }
    }

    const reductions = this.betInformation.getReductionSet().getReductions();
    let numberOfReductionsToUpdate = 0;

    for (const name in reductions) {
      const reduction = reductions[name];

      if (reduction.isInvalid()) {
        any = true;
        const reductionRequirementData = Utils.clone(reduction.data);
        validators.validateData(reductionRequirementData);
        reductionRequirementsData[name] = reductionRequirementData;
        numberOfReductionsToUpdate++;
      }
    }

    const betConfig = this.betInformation.getBetConfig();
    if (betConfig.isInvalid()) {
      any = true;
      const betConfigData = Utils.clone(betConfig.data);
      validators.validateData(betConfigData);
      request.data.betConfig = betConfigData;

      Utils.debug('Updating betConfig');
    }

    if (any) {
      Utils.debug(
        `Updating ${rowsData.length} rows and ${numberOfReductionsToUpdate} reductions.`,
      );
      //d(JSON.stringify(request.data));
    } else if (
      ![
        MANUAL_RECALC,
        DIVIDEND_RECALC,
        PRESET_REDUCTION_RECALC,
        OWNWINPERCENTAGE_RECALC,
      ].includes(betUpdateType)
    ) {
      Utils.debug('Nothing to update?');
      request = null;
    }

    return request;
  }

  onUpdateBetInformationResponse(request, callback) {
    const { data: requestData, responseData, createdTime } = request;

    const rowsData = requestData.rows;

    for (const rowData of rowsData) {
      const legNr = rowData.legNr;
      const legIndex = legNr - 1;

      const betRow = this.betInformation.getRow(legIndex);
      betRow.validate(createdTime);
    }

    const reductionSet = this.betInformation.getReductionSet();
    const reductionRequirementsData = requestData.reductionRequirements;

    for (const name in reductionRequirementsData) {
      if (name !== 'presetReduction') {
        const reduction = reductionSet.getReduction(name);
        reduction.validate(createdTime);
      }
    }

    if (requestData.betConfig) {
      this.betInformation.getBetConfig().validate(createdTime);
    }

    if (responseData.playedBet) {
      this.betInformation
        .getBetConfig()
        .setLockTime(responseData.playedBet.lockTime);
    }

    getStore().dispatch(
      changeCurrentBetPresetBasedAt(responseData.presetBetBasedAt),
    );

    this.setStake(responseData);

    Utils.callDelegate(this.delegate, 'onAPIUpdateComplete');

    if (callback) {
      callback(true);
    }
  }

  onFailedToUpdateBetInformation(error, callback) {
    Utils.callDelegate(this.delegate, 'onAPIUpdateComplete', error);
    if (callback) {
      callback(false);
    }
  }

  getLoadStakeRequest(callback) {
    const request = new Request({
      path: 'game/fetchStake',
      method: 'GET',
      onSuccess: request => {
        this.onLoadedStake(request);
        if (callback) {
          callback(true);
        }
      },
      onError: () => {
        if (callback) {
          callback(false);
        }
      },
    });

    return request;
  }

  onLoadedStake(request) {
    if (request.responseData) {
      this.setStake(request.responseData);
      Utils.callDelegate(this.delegate, 'onAPIUpdateComplete');
    }
  }

  getLoadRankingReuseRequest = callback =>
    new Request({
      path: 'archive/findRankingForReuse',
      method: 'GET',
      ...(callback != null && {
        onSuccess: request => callback(request.responseData),
      }),
      onError: () => callback(false),
    });

  applyRankingReuseRequest = savedBetMinimal => {
    new Request({
      path: 'game/applyRankingReuse',
      method: 'POST',
      data: savedBetMinimal,
      onSuccess: request => {
        if (request.responseData) {
          this.onResetBetRankingAllRows(request);
          Utils.callDelegate(this.delegate, 'onAPIUpdateComplete');
        }
      },
    }).start();
  };

  getTrendInformation(minutes) {
    return new Request({
      path: `game/fetchStakeTrend/${minutes}`,
      method: 'GET',
      onSuccess: request => {
        if (!request.responseData.trendMissing) {
          const trend = request.responseData.trend,
            periodTrend = request.responseData.period,
            latestTrend = request.responseData.latest;

          const numOfRows = this.betInformation.getNumberOfRows();
          this.betInformation
            .getBetConfig()
            .setTrendFormattedTimestamp(
              request.responseData.trendFormattedTimestamp,
            );
          for (let index = 0; index < numOfRows; index++) {
            const betRow = this.betInformation.getRow(index);
            if (betRow && periodTrend && latestTrend) {
              betRow.setCurrentTrend(trend[index]);
              betRow.setLatestTrend(latestTrend[index]);
              betRow.setPeriodTrend(periodTrend[index]);
              betRow.validate();
            }
          }
        } else {
          const numOfRows = this.betInformation.getNumberOfRows();
          for (let index = 0; index < numOfRows; index++) {
            const betRow = this.betInformation.getRow(index);
            if (betRow) {
              betRow.setCurrentTrend([]);
              betRow.setLatestTrend([]);
              betRow.setPeriodTrend([]);
              betRow.validate();
            }
          }
        }
        Utils.callDelegate(this.delegate, 'onAPIUpdatedJockeyInformation');
      },
    }).start();
  }

  resetBetRankingAllRows(rankingType) {
    new Request({
      path: `game/resetRowRanking/${rankingType}`,
      method: 'GET',
      onSuccess: this.onResetBetRankingAllRows,
    }).start();
  }

  onResetBetRankingAllRows(request) {
    if (Array.isArray(request.responseData)) {
      const orderMap = request.responseData;
      for (let row = 0; row < this.betInformation.getNumberOfRows(); row++) {
        const betRow = this.betInformation.getRow(row);
        const originalOrder = betRow.getOrder();
        const originalValues = [...betRow.getOwnReductionPercentages()];

        for (let index = 0; index < orderMap[row].length; index++) {
          const toIndex = orderMap[row][index];
          const fromIndex = originalOrder.indexOf(toIndex);
          const value = originalValues[fromIndex];

          betRow.setOwnReductionPercentage(index, value);
        }
        betRow.setOrder(orderMap[row]);
      }
      Utils.callDelegate(this.delegate, 'onAPIUpdatedJockeyInformation');
    }
  }

  recalcBet() {
    this.startBetUpdate(null, MANUAL_RECALC);
  }

  betUpdate() {
    this.startBetUpdate(null, PRESET_REDUCTION_RECALC);
  }

  // ---------------------------------------- STAKE ----------------------------------------

  resetStake() {
    this.stake = new Stake();
  }

  getStake() {
    return this.stake || new Stake();
  }

  setStake(stakeData) {
    const stake = new Stake(stakeData);

    if (stakeData) {
      if (stakeData.rowsLeft != null) {
        this.parseRowsLeftData(stakeData.rowsLeft);
      }

      const isManualUpdateButtonActive = menuSelectors.manualUpdateButtonActive(
        getStore().getState(),
      );

      if (
        (stakeData.notCalculated && !isManualUpdateButtonActive) ||
        (!stakeData.notCalculated && isManualUpdateButtonActive)
      ) {
        getStore().dispatch(toggleManualUpdateButton());
      }

      if (stakeData.presetBetBasedAt) {
        getStore().dispatch(
          changeCurrentBetPresetBasedAt(stakeData.presetBetBasedAt),
        );
      }
    }

    const oldPercentOfBets = this.stake ? this.stake.getPercentOfBets() : [];
    const newPercentOfBets = stake.getPercentOfBets();
    const oldNrOfBets = this.stake ? this.stake.getNrOfBets() : [];
    const newNrOfBets = stake.getNrOfBets();

    const changedRowIndices = [
      ...Array.from({
        length: Math.max(oldNrOfBets.length, newNrOfBets.length),
      }).keys(),
    ].filter(
      (_, idx) =>
        !isEqual(oldPercentOfBets[idx], newPercentOfBets[idx]) ||
        !isEqual(oldNrOfBets[idx], newNrOfBets[idx]),
    );

    this.stake = stake;

    changedRowIndices.forEach(idx =>
      Utils.callDelegate(this.delegate, 'onAPIStakeRowUpdated', idx),
    );
  }

  // ---------------------------------------- HORSE INFO ----------------------------------------

  loadHorseSetInformation(legNumber, callback) {
    const request = new Request({
      path: 'race/fetchHorseInfoByLegNr',
      method: 'POST',
      data: {
        legNr: legNumber,
      },
      onSuccess: function (request) {
        if (request.responseData) {
          callback(new HorseInfoSet(request.responseData));
        } else {
          callback(false);
        }
      },
      onError: function () {
        if (callback) {
          callback(false);
        }
      },
    });

    request.start();
  }

  loadHorseInformation(legNumber, startNumber, callback) {
    const request = new Request({
      path: `race/fetchSingleHorseInfo/${legNumber}/${startNumber}`,
      method: 'GET',
      onSuccess: function (request) {
        if (request.responseData) {
          callback(
            new HorseInfoSet({
              jokerHorseInfos: [request.responseData.horseInfo],
              isLoggedIn: request.responseData.isLoggedIn,
              commentsPublishDate: request.responseData.commentsPublishDate,
              sku: request.responseData.sku,
            }),
          );
        } else {
          callback(false);
        }
      },
      onError: function () {
        if (callback) {
          callback(false);
        }
      },
    });
    request.start();
  }

  // ---------------------------------------- RACE ANALYSIS ----------------------------------------

  loadRaceAnalysis(raceNumber, raceKey, callback) {
    const path = 'race/fetchHeadAnalysis';
    const cacheKey = path + raceKey;

    const cached = getCache(cacheKey);
    if (cached) {
      callback(cached);
    } else {
      const request = new Request({
        path: `${path}?raceNr=${raceNumber}`,
        onSuccess: function (request) {
          if (request.responseData) {
            const raceAnalysis = new RaceAnalysis(request.responseData);
            if (raceAnalysis.isBought()) {
              // setCache(cacheKey, raceAnalysis, CACHE_MEDIUM);
            }
            callback(raceAnalysis);
          } else {
            callback(false);
          }
        },
        onError: function (request) {
          if (callback) {
            callback(
              false,
              request.error &&
                request.error.statusCode === 503 &&
                request.error.text
                ? JSON.parse(request.error.text).message
                : null,
            );
          }
        },
      });
      request.start();
    }
  }

  // ---------------------------------------- BET ----------------------------------------

  createBet(callback, betKey, betConfig) {
    const createBetParameters = new CreateBetParameters();
    createBetParameters.setBetKey(betKey);
    createBetParameters.setBetConfig(betConfig);

    const request = new Request({
      path: 'game/createBet',
      method: 'POST',
      data: createBetParameters,
      onSuccess: request => {
        if (request.responseData) {
          this.onLoadedBetInformation(request);
          callback(request.responseData.id);
        } else {
          callback(null);
        }
      },
      onError: () => {
        callback(null);
      },
    });

    request.start();
  }

  // ---------------------------------------- BET KEY INFO ----------------------------------------

  loadBetKeyInfo(betKey, callback) {
    // check the bet key info cache first
    if (!this.betKeyInfoCache) {
      this.betKeyInfoCache = {};
    }

    const cacheKey = [
      betKey.getBetType(),
      betKey.getTrackKey(),
      betKey.getDate().getTime(),
    ].join('-');

    if (cacheKey in this.betKeyInfoCache) {
      callback(this.betKeyInfoCache[cacheKey], true);
      return;
    }

    // if a calendar is stored, search it for matches to the bet key
    const betKeyTrackKey = betKey.getTrackKey();
    const betKeyDateTime = betKey.getDate().getTime();
    const betKeyBetType = betKey.getBetType();

    if (this.calendar) {
      const numberOfRaceDay = this.calendar.getNumberOfRaceDays();

      for (
        let raceDayIndex = 0;
        raceDayIndex < numberOfRaceDay;
        raceDayIndex++
      ) {
        const raceDay = this.calendar.getRaceDay(raceDayIndex);

        if (betKeyDateTime !== raceDay.getDate().getTime()) {
          continue;
        }

        const numberOfBetTypes = raceDay.getNumberOfBetTypes();
        for (
          let betTypeIndex = 0;
          betTypeIndex < numberOfBetTypes;
          betTypeIndex++
        ) {
          const betType = raceDay.getBetType(betTypeIndex);

          if (betKeyBetType !== betType.getBetType()) {
            continue;
          }

          let track;
          let trackKey;

          if (betType.isShared()) {
            track = betType.getSharedTrack();
            trackKey = betType.getSharedTrackKey();
          } else {
            track = raceDay.getTrack();
            trackKey = raceDay.getTrackKey();
          }

          if (betKeyTrackKey !== trackKey) {
            continue;
          }

          const betKeyInfo = new BetKeyInfo();
          betKeyInfo.setTimePerRaceNumber(betType.getTimePerRaceNumber());
          betKeyInfo.setFirstPostTimePerBetType(
            betType.getFirstPostTimePerBetType(),
          );
          betKeyInfo.setTrack(track);
          betKeyInfo.setRaceNumbers(betType.getRaceNumbers());

          // store in cache
          this.betKeyInfoCache[cacheKey] = betKeyInfo;

          callback(betKeyInfo, true);
          return;
        }
      }
    }

    // if info wasn't available in the calendar, make api request for specific bet key!
    const request = new Request({
      path: 'calendar/fetchBetKeyInfo',
      data: betKey,
      method: 'POST',
      onSuccess: request => {
        if (request.responseData) {
          const betKeyInfo = new BetKeyInfo(request.responseData);
          // store in cache
          this.betKeyInfoCache[cacheKey] = betKeyInfo;
          callback(betKeyInfo, false);
        } else {
          callback(false, false);
        }
      },
      onError: function () {
        callback(false, false);
      },
    });
    request.start();
  }

  // ------------------------------------ TODAYS BEST BET ------------------------------------

  setTodaysBestBet(
    { betKey, rikstotoTrackCode, raceNumber, startNumber },
    callback,
  ) {
    const payload = {
      betKey: {
        betType: betKey.getBetType(),
        date: betKey.getDate().getTime(),
        trackKey: betKey.getTrackKey(),
      },
      rikstotoTrackCode,
      raceNr: raceNumber,
      startNr: startNumber,
    };
    const request = new Request({
      path: 'betTipAdmin/todaysBestBet',
      method: 'POST',
      data: payload,
      onSuccess: () => {
        callback();
      },
    });

    request.start();
  }

  deleteTodaysBestBet(epoch, callback) {
    const request = new Request({
      path: `betTipAdmin/todaysBestBet/${epoch}`,
      method: 'DELETE',
      onSuccess: () => {
        callback();
      },
    });
    request.start();
  }

  // ------------------------------------ WIZ EDITOR ------------------------------------

  publishWizRanking({ betId }, callback) {
    const request = new Request({
      path: `betTipAdmin/wizRanking/${betId}`,
      method: 'POST',
      onSuccess: () => {
        callback();
      },
    });

    request.start();
  }
}

const api = new API();
export default api;
